/**
 * Hook to provide folders enhanced with their actual items as children
 * 
 * This hook combines folder structure with actual items from the linkEngine,
 * creating a tree structure where items appear as children under their folders.
 */

import { useState, useEffect, useMemo } from 'react';
import { subscribeLinkEngine, getItemsByFolders, LinkEngineState } from '@/rerun-manager/linkEngine';
import { FolderItem } from '@/components/layout/side-bars/main-sidebar/organization/FolderStructure';
import { getItemTypeIcon } from '@/lib/config/pageItemConfig';

export interface UseFolderWithItemsProps {
  gameId?: string;
  itemType: 'glossary' | 'document' | 'blueprint' | 'dialog' | 'flow' | 'variable';
  folders: FolderItem[];
  showItems?: boolean;
}

export interface UseFolderWithItemsReturn {
  foldersWithItems: FolderItem[];
  isLoading: boolean;
  totalItemCount: number;
  rootItemCount: number;
  rootFolderWithItems: FolderItem | null;
}

/**
 * Get icon for different item types
 */
function getItemIcon(itemType: 'glossary' | 'document' | 'blueprint' | 'dialog' | 'flow' | 'variable'): string {
  return getItemTypeIcon(itemType);
}

/**
 * Hook that enhances folder data with actual items as children
 */
export function useFolderWithItems({
  gameId,
  itemType,
  folders,
  showItems = true,
}: UseFolderWithItemsProps): UseFolderWithItemsReturn {
  const [linkEngineState, setLinkEngineState] = useState<LinkEngineState | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Subscribe to linkEngine state changes
  useEffect(() => {
    const unsubscribe = subscribeLinkEngine((state) => {
      setLinkEngineState(state);
      setIsLoading(!state.isInitialized);
    });

    return unsubscribe;
  }, []);

  // Calculate folders with items and enhance folder data
  const { foldersWithItems, totalItemCount, rootItemCount, rootFolderWithItems } = useMemo(() => {
    if (!gameId || !linkEngineState?.isInitialized || !showItems) {
      return {
        foldersWithItems: folders,
        totalItemCount: 0,
        rootItemCount: 0,
        rootFolderWithItems: null,
      };
    }

    // Get all items organized by folders
    const itemsByFolder = getItemsByFolders(gameId, itemType);
    let totalCount = 0;

    // Count all items
    for (const items of itemsByFolder.values()) {
      totalCount += items.length;
    }

    // Get root items and create virtual root folder
    const rootItems = itemsByFolder.get('root') || [];
    const rootItemCount = rootItems.length;
    
    // Create virtual root folder with root items as children
    const rootFolderWithItems: FolderItem | null = rootItemCount > 0 ? {
      id: 'root',
      name: 'Root',
      type: 'folder' as const,
      itemCount: rootItemCount,
      children: rootItems.map(item => ({
        id: `item-${item.id}`,
        name: item.name || item.title || 'Untitled',
        type: 'item' as const,
        itemData: item,
        icon: getItemIcon(itemType),
        parentId: 'root',
      })),
    } : null;

    // Recursively enhance folders with items as children
    const enhanceFoldersWithItems = (folderList: FolderItem[]): FolderItem[] => {
      return folderList.map(folder => {
        // Get items for this folder
        const folderItems = itemsByFolder.get(folder.id) || [];
        
        // Convert items to FolderItem format
        const itemChildren: FolderItem[] = folderItems.map(item => ({
          id: `item-${item.id}`,
          name: item.name || item.title || 'Untitled',
          type: 'item' as const,
          itemData: item,
          icon: getItemIcon(itemType),
          parentId: folder.id,
        }));

        // Recursively process folder children
        const folderChildren = folder.children 
          ? enhanceFoldersWithItems(folder.children)
          : [];

        // Combine folder children with item children
        const allChildren = [...folderChildren, ...itemChildren];

        return {
          ...folder,
          type: 'folder' as const,
          children: allChildren.length > 0 ? allChildren : undefined,
          itemCount: folderItems.length, // Count of direct items in this folder
        };
      });
    };

    const enhancedFolders = enhanceFoldersWithItems(folders);

    return {
      foldersWithItems: enhancedFolders,
      totalItemCount: totalCount,
      rootItemCount,
      rootFolderWithItems,
    };
  }, [gameId, itemType, folders, linkEngineState, showItems]);

  return {
    foldersWithItems,
    isLoading,
    totalItemCount,
    rootItemCount,
    rootFolderWithItems,
  };
}
